<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.all_installations')" active></b-tab>
        </b-tabs>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
          <!-- Search and Filters Section -->
          <div class="filters-section">
            <div class="search-container">
              <label class="search-label">{{ $t("globals.type_to_search") }}</label>
              <b-form-input
                v-model="filter"
                class="search-input"
                :placeholder="$t('globals.search_for') + '...'"
              />
            </div>

            <div class="filter-group">
              <div class="filter-item">
                <label class="filter-label">{{ $t("globals.status") }}</label>
                <b-form-select
                  v-model="status"
                  :options="statusOptions"
                  class="filter-select"
                />
              </div>
            </div>

            <div class="action-buttons">
              <button
                class="reload-btn"
                @click="resetReload"
                :disabled="getInstallationFormsLoading"
                v-b-tooltip.hover
                :title="$t('globals.clear_filters')"
              >
                <icon name="sync-alt" :spin="getInstallationFormsLoading"></icon>
              </button>
            </div>
          </div>

          <!-- Table -->
          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    :key="field.key"
                    :class="{ sortable: field.sortable }"
                    @click="field.sortable && sortByField(field.key)"
                  >
                    {{ field.label || field.key }}
                    <span v-if="sortBy === field.key" class="sort-icon">
                      {{ sortDesc ? '▼' : '▲' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in paginatedItems"
                  :key="item.id"
                  class="table-row"
                >
                  <td>{{ item.id }}</td>
                  <td>{{ item.imei || '-' }}</td>
                  <td>{{ item.user || '-' }}</td>
                  <td>{{ item.license || '-' }}</td>
                  <td v-b-tooltip.hover :title="item.make">
                    {{ item.model || '-' }}
                  </td>
                  <td>{{ item.installation_type || '-' }}</td>
                  <td>
                    <span class="status-badge" :class="`status-${item.status}`">
                      {{ item.status || '-' }}
                    </span>
                  </td>
                  <td>{{ item.billing_info_type || '-' }}</td>
                  <td>{{ item.customer || '-' }}</td>
                  <td>{{ item.connection_status || '-' }}</td>
                  <td v-b-tooltip.hover :title="item.updated_at">
                    {{ item.updated_at ? item.updated_at.substring(0, 10) : '-' }}
                  </td>
                  <td v-b-tooltip.hover :title="item.created_at">
                    {{ item.created_at ? item.created_at.substring(0, 10) : '-' }}
                  </td>
                  <td v-b-tooltip.hover :title="item.notes">
                    {{
                      item.notes && item.notes.length > 20
                        ? item.notes.substring(0, 20) + "..."
                        : item.notes || '-'
                    }}
                  </td>
                  <td>
                    <b-button
                      variant="info"
                      size="sm"
                      @click="goToInstallationForm(item.id)"
                    >
                      {{ $t("globals.ins_form") }}
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="getInstallationFormsLoading" class="loading-state">
              <div class="spinner"></div>
              <p>{{ $t("globals.loading") }}...</p>
            </div>
            <div v-else-if="paginatedItems.length === 0" class="empty-state">
              <p>{{ $t("globals.no_results") }}</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-section">
            <div class="controls-left">
              <span class="summary-label">{{ $t("globals.showing") }} {{ offset + 1 }}-{{ Math.min(offset + limit, offset + paginatedItems.length) }}</span>
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="offset === 0 || getInstallationFormsLoading"
                @click="fetchLess"
              >
                &lt;
              </button>
              <button
                class="page-btn"
                :disabled="paginatedItems.length < limit || getInstallationFormsLoading"
                @click="fetchMore"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      offset: 0,
      limit: 20,
      filter: null,
      filterDebounceTimeout: null,
      skipFilterWatcher: false,
      status: "all",
      statusOptions: [
        { text: this.$t("globals.all"), value: "all" },
        { text: this.$t("globals.pending"), value: "pending" },
        { text: this.$t("globals.completed"), value: "completed" }
      ],
      sortBy: "updated_at",
      sortDesc: true,
      fields: [
        {
          key: "id",
          sortable: true
        },
        {
          key: "imei",
          sortable: true
        },
        {
          key: "user",
          sortable: true
        },
        {
          key: "license",
          sortable: false
        },
        {
          key: "make_model",
          label: this.$t("globals.make"),
          sortable: false
        },
        {
          key: "installation_type",
          label: this.$t("globals.installation_type"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: false
        },
        {
          key: "billing_info_type",
          label: "Info",
          sortable: true
        },
        {
          key: "customer",
          label: this.$t("globals.customer"),
          sortable: false
        },
        {
          key: "connection_status",
          label: this.$t("globals.connection_status"),
          sortable: true
        },
        {
          key: "updated_at",
          label: this.$t("globals.updated_at"),
          sortable: true
        },
        {
          key: "created_at",
          label: this.$t("globals.created_at"),
          sortable: true
        },
        {
          key: "notes",
          label: this.$t("globals.notes"),
          sortable: false
        },
        {
          key: "actions",
          label: this.$t("globals.actions"),
          sortable: false
        }
      ]
    };
  },
  computed: {
    ...mapGetters("installation_forms", [
      "getInstallationForms",
      "getInstallationFormsLoading"
    ]),
    ...mapGetters(["getUserBrandPermissions", "getWebAppUrl"]),

    paginatedItems() {
      return this.getInstallationForms;
    }
  },
  methods: {
    ...mapActions("installation_forms", ["fetchInstallationForms"]),
    sortByField(key) {
      if (this.sortBy === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = key;
        this.sortDesc = false;
      }
      this.offset = 0;
      this.fetch();
    },
    fetch() {
      let order_by = this.sortBy;

      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchInstallationForms({
        limit: this.limit,
        offset: this.offset,
        order_by,
        search: this.filter,
        status: this.status === "all" ? ["pending", "completed"] : [this.status]
      });
    },
    fetchMore() {
      this.offset += 20;
      this.fetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        this.fetch();
      }
    },
    goToInstallationForm(id) {
      window.open(
        `${this.getWebAppUrl}/admin/device_installations/${id}`,
        "_blank"
      );
    },
    resetReload() {
      this.offset = 0;
      this.sortBy = "updated_at";
      this.sortDesc = true;
      this.status = "all";

      if (this.filterDebounceTimeout) {
        clearTimeout(this.filterDebounceTimeout);
        this.filterDebounceTimeout = null;
      }

      this.skipFilterWatcher = true;
      this.filter = null;

      this.fetch();
    }
  },
  watch: {
    filter(newValue, oldValue) {
      if (this.skipFilterWatcher) {
        this.skipFilterWatcher = false;
        return;
      }

      if (newValue === oldValue) {
        return;
      }

      if (this.filterDebounceTimeout) {
        clearTimeout(this.filterDebounceTimeout);
      }

      this.filterDebounceTimeout = setTimeout(() => {
        this.offset = 0;
        this.fetch();
        this.filterDebounceTimeout = null;
      }, 900);
    },
    status(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      this.offset = 0;
      this.fetch();
    }
  },
  beforeDestroy() {
    if (this.filterDebounceTimeout) {
      clearTimeout(this.filterDebounceTimeout);
      this.filterDebounceTimeout = null;
    }
  },
  mounted() {
    this.fetch();
  },
  created() {
    if (!this.getUserBrandPermissions.installations_index) {
      this.$router.push({ name: "dashboard" });
    }
  }
};
</script>
